import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Divider from "@/components/Divider";
import EmptyState from "@/components/EmptyState";
import SectionHeading from "@/components/SectionHeading";
import SubSectionHeading from "@/components/SubSectionHeading";
import Table, { buildActionColumn } from "@/components/Table";
import { buildCopyTextColumn } from "@/components/Table/buildCopyTextColumn";
import { buildTagColumn } from "@/components/Table/buildTagColumn";
import { useCanManageApplications } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { sanitizeHtmlId } from "@/lib/htmlHelpers";
import type { BackgroundCheck } from "@/types";
import BackgroundCheckOrderActionsModal from "./BackgroundCheckOrderActionsModal";
import BackgroundCheckOrderSideSheet from "./BackgroundCheckOrderSideSheet";
import BackgroundCheckRequirementPanel from "./BackgroundCheckRequirementPanel";
import OrderDateTimeDisplay from "./OrderDateTimeDisplay";
import { GET_PERSON_BACKGROUND_CHECK_ORDER_QUERY } from "./queries";
import { canCreateBackgroundCheckOrder, canSendBackgroundCheckInvitation } from "./orderActionEligibility";
import {
  canCreateBackgroundCheckStaffOrder,
  canEditBackgroundCheckRequirementOnPtm,
  canSendBackgroundCheckStaffInvitation,
  canViewBackgroundCheckStaffSection
} from "./backgroundCheckEligibility";
import { getOrderLastActivity, getOrderStatusTagType } from "./orderPresentation";
import { isRequirementApproved } from "./requirementSummary";
import type { BackgroundCheckOrderAction, BackgroundCheckOrder } from "./types";
import type { BackgroundCheckFocusedPageActions } from "./BackgroundCheckFocusedPageActions";
import {
  NEW_SCREENING_ORDER_LABEL,
  QUALIFYING_LEVEL_REQUIREMENT_BODY,
  QUALIFYING_LEVEL_REQUIREMENT_HEADING,
  SCREENING_ORDERS_BODY,
  SCREENING_ORDERS_EMPTY_APPROVED,
  SCREENING_ORDERS_EMPTY_DEFAULT,
  SCREENING_ORDERS_HEADING,
  SCREENING_ORDERS_LOAD_ERROR_CAPTION,
  SCREENING_ORDERS_LOAD_ERROR_TITLE
} from "./backgroundCheckCopy";

export interface BackgroundCheckOrdersPanelProps {
  personId: string;
  pgaId: string;
  backgroundCheck?: BackgroundCheck | null;
  /** Focused page uses SectionHeading blocks; embedded PTM table uses SubSectionHeading. */
  layout?: "embedded" | "focused";
  /** Focused page registers top-bar cluster actions (edit disposition, new screening order). */
  onFocusedPageActionsChange?: (actions: BackgroundCheckFocusedPageActions | null) => void;
}

export default function BackgroundCheckOrdersPanel({
  personId,
  pgaId,
  backgroundCheck,
  layout = "embedded",
  onFocusedPageActionsChange
}: BackgroundCheckOrdersPanelProps) {
  const flags = useFlags();
  const staffSectionVisible = canViewBackgroundCheckStaffSection(flags);
  const staffSendInvitationEnabled = canSendBackgroundCheckStaffInvitation(flags);
  const staffCreateOrderEnabled = canCreateBackgroundCheckStaffOrder(flags);
  const canManageApplications = useCanManageApplications();
  const { Menu: RowMenu, setMenu } = useTableRowMenu();
  const [modalAction, setModalAction] = useState<BackgroundCheckOrderAction | null>(null);
  const [modalOrderId, setModalOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<BackgroundCheckOrder | null>(null);
  const [editDispositionOpen, setEditDispositionOpen] = useState(false);
  const canEditRequirement = canEditBackgroundCheckRequirementOnPtm(flags);

  const { data, loading, error } = useQuery(GET_PERSON_BACKGROUND_CHECK_ORDER_QUERY, {
    variables: { personId },
    skip: !staffSectionVisible || !canManageApplications,
    fetchPolicy: "cache-first"
  });

  const orders = useMemo(
    () =>
      [...(data?.person?.backgroundCheckOrders ?? [])].sort(
        (first, second) => Date.parse(second.createdAt ?? "") - Date.parse(first.createdAt ?? "")
      ),
    [data?.person?.backgroundCheckOrders]
  );
  const currentOrder = orders[0] ?? null;
  const createEnabled = staffCreateOrderEnabled && canCreateBackgroundCheckOrder(currentOrder);
  const hasRowActions = staffSendInvitationEnabled && orders.some((order) => canSendBackgroundCheckInvitation(order));
  const hasActionColumn = orders.length > 0;
  const requirementApprovedWithoutOrders = isRequirementApproved(backgroundCheck) && orders.length === 0;
  const isFocusedLayout = layout === "focused";

  const openCreateOrder = useCallback(() => {
    setModalOrderId(null);
    setModalAction("create");
  }, []);

  const openEditDisposition = useCallback(() => {
    setEditDispositionOpen(true);
  }, []);

  useEffect(() => {
    if (!isFocusedLayout || !onFocusedPageActionsChange) {
      return;
    }

    onFocusedPageActionsChange({
      canEditDisposition: canEditRequirement,
      createEnabled,
      openEditDisposition,
      openCreateOrder
    });

    return () => onFocusedPageActionsChange(null);
  }, [
    canEditRequirement,
    createEnabled,
    isFocusedLayout,
    onFocusedPageActionsChange,
    openCreateOrder,
    openEditDisposition
  ]);

  const orderColumns = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: ({ row }: { row: { original: BackgroundCheckOrder } }) => (
          <OrderDateTimeDisplay value={row.original.createdAt} />
        ),
        enableSorting: false,
        size: 200
      },
      buildTagColumn<BackgroundCheckOrder>({
        accessorKey: "status",
        header: "Status",
        getTagLabel: (row) => row.status || "Not Started",
        getTagType: (row) => getOrderStatusTagType(row.status),
        size: 180
      }),
      buildCopyTextColumn<BackgroundCheckOrder>({
        accessorKey: "providerId",
        header: "Provider ID",
        getText: (row) => row.providerId || "",
        copyFieldLabel: "Provider ID",
        enableSorting: false,
        size: 180
      }),
      {
        accessorKey: "lastActivity",
        header: "Last Activity",
        cell: ({ row }: { row: { original: BackgroundCheckOrder } }) => (
          <OrderDateTimeDisplay value={getOrderLastActivity(row.original)} />
        ),
        enableSorting: false,
        size: 220
      },
      ...(hasActionColumn
        ? [
            buildActionColumn<BackgroundCheckOrder>({
              onViewClick: (row) => setSelectedOrder(row),
              showViewButton: () => true,
              viewButtonTitle: "View screening order",
              viewButtonIcon: "open_in_full",
              getActions: (row) => {
                const actions: Action[] = [];

                if (staffSendInvitationEnabled && canSendBackgroundCheckInvitation(row)) {
                  actions.push({
                    text: "Send Invitation",
                    onClick: () => {
                      setModalOrderId(row.id);
                      setModalAction("email");
                    }
                  });
                }

                return actions;
              },
              actionId,
              setMenu,
              getRowId: (row) => sanitizeHtmlId(row.id)
            })
          ]
        : [])
    ],
    [hasActionColumn, setMenu, staffSendInvitationEnabled]
  );

  const closeModal = () => {
    setModalAction(null);
    setModalOrderId(null);
  };

  if (!staffSectionVisible || !canManageApplications) {
    return null;
  }

  return (
    <>
      {error && (
        <EmptyState
          title={isFocusedLayout ? SCREENING_ORDERS_LOAD_ERROR_TITLE : "Unable to load background checks"}
          caption={
            isFocusedLayout ? SCREENING_ORDERS_LOAD_ERROR_CAPTION : "Background check details could not be loaded."
          }
          iconName="error"
        />
      )}

      {!error && (
        <>
          {isFocusedLayout ? (
            <>
              <SectionHeading heading={QUALIFYING_LEVEL_REQUIREMENT_HEADING} body={QUALIFYING_LEVEL_REQUIREMENT_BODY} />
              <BackgroundCheckRequirementPanel
                pgaId={pgaId}
                backgroundCheck={backgroundCheck}
                showHeading={false}
                disableMetricEdit
                editOpen={editDispositionOpen}
                onEditOpenChange={setEditDispositionOpen}
              />
            </>
          ) : (
            <BackgroundCheckRequirementPanel pgaId={pgaId} backgroundCheck={backgroundCheck} />
          )}

          {isFocusedLayout ? (
            <>
              <SectionHeading heading={SCREENING_ORDERS_HEADING} body={SCREENING_ORDERS_BODY} />
              <Table
                data={orders}
                columns={orderColumns}
                loading={loading}
                data-testid="background-check-orders-table"
                renderNoDataEmptyState={() => (
                  <EmptyState
                    title="No screening orders"
                    caption={
                      requirementApprovedWithoutOrders
                        ? SCREENING_ORDERS_EMPTY_APPROVED
                        : SCREENING_ORDERS_EMPTY_DEFAULT
                    }
                    iconName="history"
                  />
                )}
              />
            </>
          ) : (
            <>
              <Divider subtle className="my-6" />

              <div className="flex w-full flex-col items-start gap-2">
                <div className="flex w-full flex-wrap items-center justify-between gap-2">
                  <SubSectionHeading>Screening Orders</SubSectionHeading>
                  {createEnabled && (
                    <Button
                      variant="outlined"
                      icon="add"
                      onClick={openCreateOrder}
                      testId="create-background-check-order-button"
                    >
                      {NEW_SCREENING_ORDER_LABEL}
                    </Button>
                  )}
                </div>

                <Table
                  data={orders}
                  columns={orderColumns}
                  loading={loading}
                  data-testid="background-check-orders-table"
                  renderNoDataEmptyState={() => (
                    <EmptyState
                      title="No screening orders"
                      caption={
                        requirementApprovedWithoutOrders
                          ? SCREENING_ORDERS_EMPTY_APPROVED
                          : SCREENING_ORDERS_EMPTY_DEFAULT
                      }
                      iconName="history"
                    />
                  )}
                />
              </div>
            </>
          )}
        </>
      )}

      <BackgroundCheckOrderActionsModal
        action={modalAction}
        open={modalAction != null}
        onClose={closeModal}
        personId={personId}
        orderId={modalOrderId ?? undefined}
        refetchQueries={[GET_PERSON_BACKGROUND_CHECK_ORDER_QUERY]}
      />
      <BackgroundCheckOrderSideSheet
        open={selectedOrder != null}
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />
      {hasRowActions && <RowMenu />}
    </>
  );
}
